// Activates the existing native tabs without replacing their Alpine component.
if (!customElements.get('latrand-tab-autoplay')) {
  customElements.define('latrand-tab-autoplay', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.root = this.closest('[x-data]');
      this.button = this.querySelector('button');
      if (!this.root || !this.button) return;
      this.controller = new AbortController();
      this.delay = Math.max(3, Number(this.dataset.interval) || 8) * 1000;
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.visible = false;
      const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: this.controller.signal });
      listen(this.button, 'click', () => {
        this.paused = !this.paused;
        this.updatePauseControl();
        this.schedule();
      });
      listen(this.root, 'pointerenter', event => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); }
      });
      listen(this.root, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      listen(this.root, 'focusin', event => { this.focused = event.target !== this.button; this.schedule(); });
      listen(this.root, 'focusout', () => queueMicrotask(() => {
        if (!this.isConnected) return;
        this.focused = this.root.contains(document.activeElement) && document.activeElement !== this.button;
        this.schedule();
      }));
      listen(this.root, 'click', event => {
        if (!this.advancing && event.target.closest('[data-autoplay-tab]')) this.schedule();
      });
      listen(document, 'visibilitychange', () => this.schedule());
      listen(document, 'shopify:block:select', event => {
        if (event.detail?.sectionId === this.root.closest('[data-section-id]')?.dataset.sectionId) {
          this.paused = true;
          this.updatePauseControl();
          this.schedule();
        }
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
      });
      this.observer.observe(this.root);
    }
    updatePauseControl() {
      this.button.setAttribute('aria-pressed', String(this.paused));
      this.button.setAttribute('aria-label', this.paused ? this.dataset.resumeLabel : this.dataset.pauseLabel);
      this.button.querySelector('[data-pause-icon]').toggleAttribute('hidden', this.paused);
      this.button.querySelector('[data-play-icon]').toggleAttribute('hidden', !this.paused);
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || this.paused || this.hovered || this.focused || !this.visible || document.hidden) return;
      this.timer = setTimeout(() => { this.advance(); this.schedule(); }, this.delay);
    }
    advance() {
      let tabs = [...this.root.querySelectorAll('.tabs__button-horizontal[data-autoplay-tab]')];
      if (!tabs.length) tabs = [...this.root.querySelectorAll('.tabs__button-vertical-item[data-autoplay-tab]')];
      if (tabs.length < 2) return;
      const index = tabs.findIndex(tab => tab.classList.contains('is-active') || tab.getAttribute('aria-selected') === 'true');
      const next = tabs[(index + 1) % tabs.length];
      this.advancing = true;
      next.click();
      this.advancing = false;
      const nav = next.closest('.tab-list');
      if (nav) {
        const tabRect = next.getBoundingClientRect(), navRect = nav.getBoundingClientRect();
        if (tabRect.left < navRect.left || tabRect.right > navRect.right) {
          nav.scrollBy({ left: tabRect.left - navRect.left - (nav.clientWidth - tabRect.width) / 2,
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
